if (!customElements.get('filter-finder-quiz')) {
  customElements.define(
    'filter-finder-quiz',
    class FilterFinderQuiz extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.modalId = this.dataset.quizInstance || 'global';
        this.modalHost = this.closest('[data-filter-finder-quiz-modal]');
        this.isModalInstance = this.dataset.quizModalInstance === 'true' && this.modalHost;
        this.config = this.parseConfig();
        this.answers = this.getInitialAnswers();
        this.started = false;
        this.currentStep = 's1';
        this.currentResult = null;
        this.lastSubmittedSignature = '';
        this.lastInterestSignature = '';
        this.introScreen = this.querySelector('[data-quiz-intro]');
        this.panel = this.querySelector('[data-quiz-panel]');
        this.startButton = this.querySelector('[data-quiz-start]');
        this.optionButtons = Array.from(this.querySelectorAll('[data-step-option]'));
        this.nextButtons = Array.from(this.querySelectorAll('[data-next]'));
        this.backButtons = Array.from(this.querySelectorAll('[data-back]'));
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.resultContainer = this.querySelector('[data-result]');
        this.liveRegion = this.querySelector('[data-live-region]');
        this.leadFields = Array.from(this.querySelectorAll('[data-lead-field]'));

        this.onStartClick = this.handleStartClick.bind(this);
        this.onOptionClick = this.handleOptionClick.bind(this);
        this.onNextClick = this.handleNextClick.bind(this);
        this.onBackClick = this.handleBackClick.bind(this);
        this.onLeadInput = this.handleLeadInput.bind(this);
        this.onResultClick = this.handleResultClick.bind(this);
        this.onResultChange = this.handleResultChange.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onModalOpen = this.handleModalOpen.bind(this);

        this.startButton?.addEventListener('click', this.onStartClick);
        this.optionButtons.forEach((button) => button.addEventListener('click', this.onOptionClick));
        this.nextButtons.forEach((button) => button.addEventListener('click', this.onNextClick));
        this.backButtons.forEach((button) => button.addEventListener('click', this.onBackClick));
        this.leadFields.forEach((field) => field.addEventListener('input', this.onLeadInput));
        this.resultContainer?.addEventListener('click', this.onResultClick);
        this.resultContainer?.addEventListener('change', this.onResultChange);

        if (this.isModalInstance) {
          document.addEventListener('click', this.onDocumentClick);
          this.modalHost.addEventListener('dialog:open', this.onModalOpen);
        }

        this.syncState();
        this.showIntro();
      }

      disconnectedCallback() {
        this.startButton?.removeEventListener('click', this.onStartClick);
        this.optionButtons?.forEach((button) => button.removeEventListener('click', this.onOptionClick));
        this.nextButtons?.forEach((button) => button.removeEventListener('click', this.onNextClick));
        this.backButtons?.forEach((button) => button.removeEventListener('click', this.onBackClick));
        this.leadFields?.forEach((field) => field.removeEventListener('input', this.onLeadInput));
        this.resultContainer?.removeEventListener('click', this.onResultClick);
        this.resultContainer?.removeEventListener('change', this.onResultChange);

        if (this.isModalInstance) {
          document.removeEventListener('click', this.onDocumentClick);
          this.modalHost?.removeEventListener('dialog:open', this.onModalOpen);
        }
      }

      getDefaultConfig() {
        return {
          labels: {
            shop: '',
            preorder: '',
            waitlist: '',
            restart: '',
            match: '',
            bestMatch: '',
            alsoConsider: '',
            preorderLabel: '',
            waitlistLabel: '',
            interestLabel: '',
            interestNote: '',
            interestAction: '',
            interestConfirmation: '',
            saving: '',
          },
          copy: {},
          answerLabels: {},
          env: {
            quizVersion: '1.2',
            detectedCountry: null,
          },
          klaviyo: {
            publicApiKey: '',
            listId: '',
            revision: '2026-04-15',
            eventName: 'quiz_complete',
            interestEventName: 'quiz_interest_update',
            source: '',
          },
          outcomes: {},
          outcomeMeta: {},
        };
      }

      getInitialAnswers() {
        return {
          s1: [],
          interestedProducts: [],
          savedInterestStates: {},
          lead: {
            name: '',
            email: '',
          },
        };
      }

      parseConfig() {
        const defaults = this.getDefaultConfig();
        const configElement = this.querySelector('[data-quiz-config]');

        if (!configElement) return defaults;

        try {
          const parsed = JSON.parse(configElement.textContent);

          return {
            labels: {
              ...defaults.labels,
              ...(parsed.labels || {}),
            },
            copy: {
              ...defaults.copy,
              ...(parsed.copy || {}),
            },
            answerLabels: {
              ...defaults.answerLabels,
              ...(parsed.answerLabels || {}),
            },
            env: {
              ...defaults.env,
              ...(parsed.env || {}),
            },
            klaviyo: {
              ...defaults.klaviyo,
              ...(parsed.klaviyo || {}),
            },
            outcomes: {
              ...(parsed.outcomes || {}),
            },
            outcomeMeta: {
              ...defaults.outcomeMeta,
              ...(parsed.outcomeMeta || {}),
            },
          };
        } catch (error) {
          return defaults;
        }
      }

      handleStartClick() {
        this.started = true;

        if (this.introScreen) this.introScreen.hidden = true;
        if (this.panel) this.panel.hidden = false;

        this.showStep('s1');
      }

      handleDocumentClick(event) {
        const trigger = event.target instanceof Element ? event.target.closest('[data-open-filter-finder-quiz]') : null;

        if (!trigger || !this.isModalInstance) return;
        if (
          event instanceof MouseEvent
          && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        ) {
          return;
        }

        event.preventDefault();

        const openModal = () => {
          if (typeof this.modalHost?.showDialog === 'function') {
            this.modalHost.showDialog();
          }
        };

        const headerDrawer = trigger.closest('header-drawer');
        if (headerDrawer && typeof headerDrawer.close === 'function') {
          headerDrawer.close();
          window.setTimeout(openModal, 180);
          return;
        }

        openModal();
      }

      handleModalOpen() {
        window.requestAnimationFrame(() => {
          const focusTarget =
            this.querySelector('.filter-finder-quiz__step.is-active [data-step-option].is-selected')
            || this.querySelector('.filter-finder-quiz__step.is-active [data-step-option]')
            || this.querySelector('.filter-finder-quiz__step.is-active [data-next]:not([disabled])')
            || this.startButton;

          if (focusTarget instanceof HTMLElement) {
            focusTarget.focus();
          }
        });
      }

      handleOptionClick(event) {
        const button = event.currentTarget;

        if (!(button instanceof HTMLButtonElement)) return;

        const step = button.dataset.stepOption;
        const value = button.dataset.value;

        if (!step || !value) return;

        if (step === 's1') {
          this.toggleScopeValue(value);
        } else {
          this.answers[step] = value;
        }

        this.pruneAnswers();
        this.syncState();
      }

      handleLeadInput(event) {
        const input = event.currentTarget;

        if (!(input instanceof HTMLInputElement)) return;

        const field = input.dataset.leadField;

        if (!field) return;

        this.answers.lead = {
          ...(this.answers.lead || {}),
          [field]: input.value,
        };

        this.syncState();
      }

      async handleNextClick(event) {
        const button = event.currentTarget;

        if (!(button instanceof HTMLButtonElement)) return;

        const currentStep = button.dataset.next;

        if (!currentStep || !this.isStepComplete(currentStep)) return;

        if (currentStep === 'lead') {
          const result = this.getResult();
          await this.submitQuizToKlaviyo(result, button);
          this.showResult(result);
          return;
        }

        const nextStep = this.getNextStep(currentStep);

        if (nextStep) {
          this.showStep(nextStep);
        }
      }

      handleBackClick(event) {
        const button = event.currentTarget;

        if (!(button instanceof HTMLButtonElement)) return;

        const step = button.dataset.back;

        if (!step) return;

        const previousStep = this.getPreviousStep(step);

        if (previousStep) {
          this.showStep(previousStep);
        }
      }

      handleResultClick(event) {
        const saveInterestButton = event.target instanceof Element ? event.target.closest('[data-quiz-interest-save]') : null;

        if (saveInterestButton instanceof HTMLButtonElement) {
          event.preventDefault();
          this.handleInterestSave(saveInterestButton);
          return;
        }

        const restartButton = event.target instanceof Element ? event.target.closest('[data-quiz-restart]') : null;

        if (!restartButton) return;

        event.preventDefault();
        this.restart();
      }

      handleResultChange(event) {
        const input = event.target;

        if (!(input instanceof HTMLInputElement)) return;
        if (!input.matches('[data-quiz-interest]')) return;

        const outcomeKey = input.dataset.outcomeKey;

        if (!outcomeKey) return;

        const interestedProducts = new Set(this.getInterestedProducts());

        if (input.checked) {
          interestedProducts.add(outcomeKey);
        } else {
          interestedProducts.delete(outcomeKey);
        }

        this.answers.interestedProducts = Array.from(interestedProducts);

        const interestWrap = input.closest('.filter-finder-quiz__interest-wrap');
        const isSaved = this.getSavedInterestState(outcomeKey);
        const isDirty = input.checked !== isSaved;
        const saveButton = interestWrap?.querySelector('[data-quiz-interest-save]');

        interestWrap?.classList.toggle('is-selected', input.checked);
        interestWrap?.classList.toggle('is-dirty', isDirty);
        interestWrap?.classList.toggle('is-saved', !isDirty && isSaved);

        if (saveButton instanceof HTMLButtonElement) {
          saveButton.disabled = !isDirty;
        }
      }

      handleInterestSave(button) {
        if (!(button instanceof HTMLButtonElement)) return;

        const outcomeKey = button.dataset.outcomeKey;

        if (!outcomeKey) return;

        const interestWrap = button.closest('.filter-finder-quiz__interest-wrap');
        const checkbox = interestWrap?.querySelector('[data-quiz-interest]');

        if (!(checkbox instanceof HTMLInputElement)) return;

        this.submitInterestToKlaviyo(outcomeKey, checkbox.checked, button, interestWrap);
      }

      showIntro() {
        this.started = false;
        this.currentStep = 's1';
        this.currentResult = null;

        if (this.introScreen) this.introScreen.hidden = false;
        if (this.panel) this.panel.hidden = true;

        if (this.resultContainer) {
          this.resultContainer.hidden = true;
          this.resultContainer.innerHTML = '';
        }

        this.steps.forEach((step) => {
          const isFirstStep = step.dataset.step === 's1';
          step.hidden = !isFirstStep;
          step.classList.toggle('is-active', isFirstStep);
        });

        if (this.liveRegion) {
          this.liveRegion.textContent = '';
        }

        this.syncStepMeta();
        this.updateProgress();
      }

      showStep(stepId) {
        this.currentStep = stepId;
        this.currentResult = null;

        if (this.introScreen) this.introScreen.hidden = true;
        if (this.panel) this.panel.hidden = false;

        if (this.resultContainer) {
          this.resultContainer.hidden = true;
          this.resultContainer.innerHTML = '';
        }

        this.steps.forEach((step) => {
          const isActive = step.dataset.step === stepId;
          step.hidden = !isActive;
          step.classList.toggle('is-active', isActive);
        });

        this.syncStepMeta();
        this.updateProgress();
      }

      showResult(result = this.getResult()) {
        this.currentResult = result;

        this.steps.forEach((step) => {
          step.hidden = true;
          step.classList.remove('is-active');
        });

        if (this.resultContainer) {
          this.resultContainer.innerHTML = this.buildResultMarkup(result);
          this.resultContainer.hidden = false;
        }

        this.currentStep = 'result';
        this.updateProgress();

        if (this.liveRegion) {
          this.liveRegion.textContent = `${result.title}. ${result.description}`;
        }
      }

      restart() {
        this.answers = this.getInitialAnswers();
        this.currentResult = null;
        this.lastSubmittedSignature = '';
        this.lastInterestSignature = '';
        this.syncState();
        this.showIntro();
      }

      toggleScopeValue(value) {
        const selectedValues = new Set(this.answers.s1 || []);

        if (value === 'both') {
          if (selectedValues.has('both')) {
            selectedValues.delete('both');
          } else {
            selectedValues.clear();
            selectedValues.add('both');
          }
        } else {
          selectedValues.delete('both');

          if (selectedValues.has(value)) {
            selectedValues.delete(value);
          } else {
            selectedValues.add(value);
          }
        }

        this.answers.s1 = Array.from(selectedValues);
      }

      getNormalizedFilterScope() {
        const selectedValues = this.answers.s1 || [];

        if (selectedValues.includes('both')) {
          return ['drinking', 'shower'];
        }

        return selectedValues.slice();
      }

      wantsDrinking() {
        return this.getNormalizedFilterScope().includes('drinking');
      }

      wantsShower() {
        return this.getNormalizedFilterScope().includes('shower');
      }

      pruneAnswers() {
        if (!this.wantsDrinking()) {
          delete this.answers.s2;
          delete this.answers.s3;
          delete this.answers.s6;
        }

        if (!this.wantsShower()) {
          delete this.answers.s5;
        }
      }

      isLeadComplete() {
        const lead = this.answers.lead || {};

        return Boolean(lead.name?.trim() && lead.email?.trim());
      }

      isStepComplete(step) {
        if (step === 'lead') return this.isLeadComplete();

        const answer = this.answers[step];

        if (Array.isArray(answer)) return answer.length > 0;

        return Boolean(answer);
      }

      getSequence() {
        const sequence = ['s1'];

        if (this.wantsDrinking()) {
          sequence.push('s2', 's3');
        }

        sequence.push('s4');

        if (this.wantsShower()) {
          sequence.push('s5');
        }

        if (this.wantsDrinking()) {
          sequence.push('s6');
        }

        sequence.push('lead');

        return sequence;
      }

      getNextStep(step) {
        const sequence = this.getSequence();
        const index = sequence.indexOf(step);

        if (index === -1) return null;

        return sequence[index + 1] || null;
      }

      getPreviousStep(step) {
        const sequence = this.getSequence();
        const index = sequence.indexOf(step);

        if (index <= 0) return null;

        return sequence[index - 1] || null;
      }

      syncState() {
        this.optionButtons.forEach((button) => {
          const step = button.dataset.stepOption;
          const value = button.dataset.value;

          if (!step || !value) return;

          let isSelected = false;

          if (step === 's1') {
            isSelected = (this.answers.s1 || []).includes(value);
          } else {
            isSelected = this.answers[step] === value;
          }

          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.leadFields.forEach((field) => {
          if (!(field instanceof HTMLInputElement)) return;

          const key = field.dataset.leadField;
          const nextValue = key ? this.answers.lead?.[key] || '' : '';

          if (field.value !== nextValue) {
            field.value = nextValue;
          }
        });

        this.nextButtons.forEach((button) => {
          const step = button.dataset.next;

          if (!step) return;

          button.disabled = !this.isStepComplete(step);
        });

        this.syncStepMeta();
        this.updateProgress();
      }

      syncStepMeta() {
        const activeStep = this.steps.find((step) => step.dataset.step === this.currentStep);

        if (!activeStep) return;

        const sequence = this.getSequence();
        const stepNumber = sequence.indexOf(this.currentStep) + 1;
        const totalSteps = sequence.length;
        const indexOutput = activeStep.querySelector('[data-step-index]');
        const totalOutput = activeStep.querySelector('[data-step-total]');

        if (indexOutput) indexOutput.textContent = String(stepNumber > 0 ? stepNumber : 1);
        if (totalOutput) totalOutput.textContent = String(totalSteps);
      }

      updateProgress() {
        if (!this.progressFill) return;

        if (!this.started) {
          this.progressFill.style.width = '0%';
          return;
        }

        if (this.resultContainer && !this.resultContainer.hidden) {
          this.progressFill.style.width = '100%';
          return;
        }

        const sequence = this.getSequence();
        const totalSteps = sequence.length || 1;
        const currentIndex = sequence.indexOf(this.currentStep);
        const currentStep = currentIndex >= 0 ? currentIndex + 1 : 1;
        const progress = Math.max((currentStep / totalSteps) * 100, 8);

        this.progressFill.style.width = `${Math.round(progress)}%`;
      }

      getGravityCartridgeKey() {
        const concern = this.answers.s4;

        return concern === 'hard' || concern === 'all' ? 'uf' : 'carbon';
      }

      getGravitySizeKey() {
        const people = this.answers.s3;

        if (people === '34') return '8';
        if (people === '5') return '12';

        return '6';
      }

      getGravityOutcomeKey(sizeKey = this.getGravitySizeKey(), cartridgeKey = this.getGravityCartridgeKey()) {
        return `gravity_${sizeKey}l_${cartridgeKey}`;
      }

      getDrinkingRecommendation() {
        const tenure = this.answers.s2;
        const people = this.answers.s3;
        const concern = this.answers.s4;
        const format = this.answers.s6 || 'either';
        const bigHousehold = people === '34' || people === '5';
        const gravityOutcomeKey = this.getGravityOutcomeKey();

        if (tenure === 'own' && bigHousehold && concern === 'all') {
          return {
            primary: 'under_counter',
            secondary: 'gravity_12l_uf',
          };
        }

        if (format === 'faucet') {
          return {
            primary: 'faucet',
          };
        }

        if (format === 'gravity') {
          return {
            primary: gravityOutcomeKey,
          };
        }

        if (format === 'either' && tenure === 'rent') {
          return {
            primary: gravityOutcomeKey,
          };
        }

        if (format === 'either' && tenure === 'own' && bigHousehold) {
          return {
            primary: gravityOutcomeKey,
          };
        }

        return {
          primary: gravityOutcomeKey,
        };
      }

      getShowerRecommendation() {
        const showerType = this.answers.s5 || 'either';

        if (showerType === 'fixed') {
          return {
            primary: 'shower_fixed',
            secondary: 'shower_handheld',
          };
        }

        return {
          primary: 'shower_handheld',
          secondary: 'shower_fixed',
        };
      }

      getOutcomeStatus(outcomeKey) {
        const outcome = this.config.outcomes?.[outcomeKey];

        if (outcome?.status) return outcome.status;

        if (outcomeKey === 'under_counter') return 'preorder';
        if (outcomeKey.startsWith('shower_')) return 'waitlist';

        return 'live';
      }

      getOutcomeNote(outcomeKey) {
        const status = this.getOutcomeStatus(outcomeKey);

        if (status === 'live') return '';

        return this.config.outcomes?.[outcomeKey]?.note || '';
      }

      getOutcomeProduct(outcomeKey) {
        return this.config.outcomes?.[outcomeKey]?.product || {};
      }

      getOutcomeFallbackImage(outcomeKey) {
        return this.config.outcomes?.[outcomeKey]?.fallbackImage || '';
      }

      hasCatalogProduct(outcomeKey) {
        const product = this.getOutcomeProduct(outcomeKey);

        return Boolean(product && typeof product === 'object' && (product.handle || product.title || product.image || product.price));
      }

      getInterestedProducts() {
        return Array.isArray(this.answers.interestedProducts) ? this.answers.interestedProducts.filter(Boolean) : [];
      }

      getSavedInterestState(outcomeKey) {
        return Boolean(this.answers.savedInterestStates?.[outcomeKey]);
      }

      getPrimaryRecommendedCartridge(outcomeKey) {
        if (typeof outcomeKey !== 'string') return null;

        if (outcomeKey.includes('_uf')) return 'uf_anti';
        if (outcomeKey.includes('_carbon')) return 'carbon';

        return null;
      }

      getResult() {
        const drinking = this.wantsDrinking();
        const shower = this.wantsShower();
        const drinkingRecommendation = drinking ? this.getDrinkingRecommendation() : null;
        const showerRecommendation = shower ? this.getShowerRecommendation() : null;

        if (drinking && shower) {
          const recommendedProducts = [showerRecommendation.primary, drinkingRecommendation.primary];

          return {
            title: this.getCopy('combinedTitle'),
            description: this.getCopy('combinedDescription'),
            answerPills: this.buildAnswerPills(),
            primaryOutcome: drinkingRecommendation.primary,
            recommendedProducts,
            groups: [
              {
                title: this.getCopy('combinedShowerGroupTitle'),
                cards: [this.createOutcomeCard(showerRecommendation.primary, { primary: true })],
              },
              {
                title: this.getCopy('combinedDrinkingGroupTitle'),
                cards: [this.createOutcomeCard(drinkingRecommendation.primary, { primary: true })],
              },
            ],
          };
        }

        if (shower) {
          const cards = [this.createOutcomeCard(showerRecommendation.primary, { primary: true })];

          if (showerRecommendation.secondary) {
            cards.push(
              this.createOutcomeCard(showerRecommendation.secondary, {
                primary: false,
                badge: this.config.labels.alsoConsider,
                badgeTone: 'alt',
              })
            );
          }

          return {
            title: this.getCopy('showerTitle'),
            description: this.getCopy('showerDescription'),
            answerPills: this.buildAnswerPills(),
            primaryOutcome: showerRecommendation.primary,
            recommendedProducts: cards.length > 1 ? [showerRecommendation.primary, showerRecommendation.secondary] : [showerRecommendation.primary],
            groups: [
              {
                cards,
              },
            ],
          };
        }

        const cards = [this.createOutcomeCard(drinkingRecommendation.primary, { primary: true })];

        if (drinkingRecommendation.secondary) {
          cards.push(
            this.createOutcomeCard(drinkingRecommendation.secondary, {
              primary: false,
              badge: this.config.labels.alsoConsider,
              badgeTone: 'alt',
            })
          );
        }

        return {
          title: this.getSingleDrinkingTitle(drinkingRecommendation.primary),
          description: this.getSingleDrinkingDescription(drinkingRecommendation.primary),
          answerPills: this.buildAnswerPills(),
          primaryOutcome: drinkingRecommendation.primary,
          recommendedProducts: drinkingRecommendation.secondary
            ? [drinkingRecommendation.primary, drinkingRecommendation.secondary]
            : [drinkingRecommendation.primary],
          groups: [
            {
              cards,
            },
          ],
        };
      }

      getSingleDrinkingTitle(outcomeKey) {
        if (outcomeKey === 'faucet') {
          return this.getCopy('faucetTitle');
        }

        if (outcomeKey === 'under_counter') {
          return this.getCopy('underCounterTitle');
        }

        if (typeof outcomeKey === 'string' && outcomeKey.startsWith('gravity_')) {
          const sizeKey = outcomeKey.includes('12l') ? '12L' : outcomeKey.includes('8l') ? '8L' : '6L';
          const template = this.getCopy('gravityTitleTemplate');

          return template.replace(/\[size\]/g, sizeKey);
        }

        return this.getCopy('fallbackTitle');
      }

      getSingleDrinkingDescription(outcomeKey) {
        if (outcomeKey === 'faucet') {
          return this.getCopy('faucetDescription');
        }

        if (outcomeKey === 'under_counter') {
          return this.getCopy('underCounterDescription');
        }

        if (typeof outcomeKey === 'string' && outcomeKey.startsWith('gravity_')) {
          return this.getCopy('gravityDescription');
        }

        return this.getCopy('fallbackDescription');
      }

      buildAnswerPills() {
        const labelMap = this.config.answerLabels || {};

        const pills = [];

        (this.answers.s1 || []).forEach((value) => {
          if (labelMap.s1[value]) pills.push(labelMap.s1[value]);
        });

        ['s2', 's3', 's4', 's5', 's6'].forEach((step) => {
          const value = this.answers[step];

          if (value && labelMap[step]?.[value]) {
            pills.push(labelMap[step][value]);
          }
        });

        return pills;
      }

      buildResultMarkup(result) {
        const totalCards = result.groups.reduce((count, group) => count + (Array.isArray(group.cards) ? group.cards.length : 0), 0);
        const isTwoGroupPair =
          totalCards === 2
          && result.groups.length === 2
          && result.groups.every((group) => Array.isArray(group.cards) && group.cards.length === 1);
        const groupsMarkup = result.groups
          .map((group) => {
            const cardsMarkup = group.cards.join('');
            const cardCount = Array.isArray(group.cards) ? group.cards.length : 0;

            return `
              <section class="filter-finder-quiz__result-group">
                ${
                  group.title
                    ? `<h3 class="filter-finder-quiz__result-group-title">${this.escapeHtml(group.title)}</h3>`
                    : ''
                }
                <div class="filter-finder-quiz__result-grid${cardCount > 1 ? ' filter-finder-quiz__result-grid--multi' : ' filter-finder-quiz__result-grid--single'}">${cardsMarkup}</div>
              </section>
            `;
          })
          .join('');

        const pillsMarkup = result.answerPills.length
          ? `
            <div class="filter-finder-quiz__result-pills">
              ${result.answerPills
                .map((pill) => `<span class="filter-finder-quiz__result-pill">${this.escapeHtml(pill)}</span>`)
                .join('')}
            </div>
          `
          : '';
        const resultTagMarkup = this.config.labels.match
          ? `<span class="filter-finder-quiz__result-tag">${this.escapeHtml(this.config.labels.match)}</span>`
          : '';
        const restartMarkup = this.config.labels.restart
          ? `
            <div class="filter-finder-quiz__result-actions">
              <button class="filter-finder-quiz__button filter-finder-quiz__restart" type="button" data-quiz-restart>
                ${this.escapeHtml(this.config.labels.restart)}
              </button>
            </div>
          `
          : '';

        return `
          <div class="filter-finder-quiz__result-head">
            ${resultTagMarkup}
            <h3 class="filter-finder-quiz__result-title">${this.escapeHtml(result.title)}</h3>
            <p class="filter-finder-quiz__result-body">${this.escapeHtml(result.description)}</p>
            ${pillsMarkup}
          </div>
          <div class="filter-finder-quiz__result-groups${totalCards === 1 ? ' filter-finder-quiz__result-groups--single' : ''}${isTwoGroupPair ? ' filter-finder-quiz__result-groups--pair' : ''}">${groupsMarkup}</div>
          ${restartMarkup}
        `;
      }

      createOutcomeCard(outcomeKey, options = {}) {
        const meta = this.getOutcomeMeta(outcomeKey);
        const product = this.getOutcomeProduct(outcomeKey);
        const fallbackImage = this.getOutcomeFallbackImage(outcomeKey);
        const status = this.getOutcomeStatus(outcomeKey);
        const note = this.getOutcomeNote(outcomeKey);
        const badge = options.badge
          || (status === 'preorder'
            ? this.config.labels.preorderLabel
            : status === 'waitlist'
              ? this.config.labels.waitlistLabel
              : options.primary
                ? this.config.labels.bestMatch
                : this.config.labels.alsoConsider);
        const badgeTone = options.badgeTone
          || (status === 'preorder' ? 'preorder' : status === 'waitlist' ? 'soon' : options.primary ? 'best' : 'alt');
        const ctaLabel = status === 'preorder'
          ? this.config.labels.preorder
          : status === 'waitlist'
            ? this.config.labels.waitlist
            : this.config.labels.shop;

        return this.createCard({
          outcomeKey,
          badge,
          badgeTone,
          icon: this.getIconMarkup(meta.icon),
          title: meta.title,
          subtitle: meta.subtitle,
          features: meta.features,
          note,
          product,
          fallbackImage,
          ctaLabel,
          primary: Boolean(options.primary),
        });
      }

      getCopy(key) {
        return String(this.config.copy?.[key] || '');
      }

      getOutcomeIconType(outcomeKey) {
        if (outcomeKey === 'faucet') return 'faucet';
        if (outcomeKey === 'under_counter') return 'underCounter';
        if (typeof outcomeKey === 'string' && outcomeKey.startsWith('shower_')) return 'shower';

        return 'gravity';
      }

      getOutcomeMeta(outcomeKey) {
        const configuredMeta = this.config.outcomeMeta?.[outcomeKey] || {};
        const features = Array.isArray(configuredMeta.features)
          ? configuredMeta.features
          : String(configuredMeta.features || '').split(/\r?\n/);

        return {
          icon: this.getOutcomeIconType(outcomeKey),
          title: configuredMeta.title || this.getCopy('fallbackTitle'),
          subtitle: configuredMeta.subtitle || '',
          features: features.map((feature) => String(feature || '').trim()).filter(Boolean),
        };
      }

      createCard({ outcomeKey, badge, badgeTone, icon, title, subtitle, features, note, product, fallbackImage, ctaLabel, primary }) {
        const resolvedProduct = product && typeof product === 'object' ? product : {};
        const resolvedTitle = title || resolvedProduct.title || this.getCopy('fallbackTitle');
        const resolvedVariantTitle = subtitle || '';
        const resolvedUrl = resolvedProduct.url || '';
        const resolvedImage = resolvedProduct.image || fallbackImage || '';
        const resolvedImageAlt = resolvedProduct.imageAlt || resolvedTitle;
        const resolvedPrice = resolvedProduct.price || '';
        const resolvedComparePrice = resolvedProduct.comparePrice || '';
        const hasCatalogProduct = Boolean(resolvedProduct.handle || resolvedProduct.title || resolvedProduct.image || resolvedProduct.price);
        const isInterested = this.getInterestedProducts().includes(outcomeKey);
        const isInterestSaved = this.getSavedInterestState(outcomeKey);
        const isInterestDirty = isInterested !== isInterestSaved;
        const safeFeatures = Array.isArray(features) ? features : [];

        const featuresMarkup = safeFeatures.length
          ? `
            <div class="filter-finder-quiz__result-copy">
              ${safeFeatures.map((feature) => `<p class="filter-finder-quiz__result-copy-item">${this.escapeHtml(feature)}</p>`).join('')}
            </div>
          `
          : '';

        const priceMarkup = resolvedPrice
          ? `
            <div class="filter-finder-quiz__result-price-row">
              <span class="filter-finder-quiz__result-price">${this.escapeHtml(resolvedPrice)}</span>
              ${
                resolvedComparePrice
                  ? `<span class="filter-finder-quiz__result-compare-price">${this.escapeHtml(resolvedComparePrice)}</span>`
                  : ''
              }
            </div>
          `
          : '';

        const noteMarkup = note ? `<p class="filter-finder-quiz__result-note">${this.escapeHtml(note)}</p>` : '';
        const interestMarkup = !hasCatalogProduct
          ? `
            <div class="filter-finder-quiz__interest-wrap${isInterested ? ' is-selected' : ''}${isInterestSaved && !isInterestDirty ? ' is-saved' : ''}${isInterestDirty ? ' is-dirty' : ''}">
              <label class="filter-finder-quiz__interest-box">
                <input
                  class="filter-finder-quiz__interest-input"
                  type="checkbox"
                  data-quiz-interest
                  data-outcome-key="${this.escapeAttribute(outcomeKey)}"
                  ${isInterested ? 'checked' : ''}
                >
                <span class="filter-finder-quiz__interest-check" aria-hidden="true"></span>
                <span class="filter-finder-quiz__interest-copy">
                  <span class="filter-finder-quiz__interest-title">${this.escapeHtml(
                    this.config.labels.interestLabel || ''
                  )}</span>
                  <span class="filter-finder-quiz__interest-text">${this.escapeHtml(
                    this.config.labels.interestNote || ''
                  )}</span>
                </span>
              </label>
              <button
                class="filter-finder-quiz__interest-save"
                type="button"
                data-quiz-interest-save
                data-outcome-key="${this.escapeAttribute(outcomeKey)}"
                ${isInterestDirty ? '' : 'disabled'}
              >
                ${this.escapeHtml(this.config.labels.interestAction || '')}
              </button>
              <div class="filter-finder-quiz__interest-confirmation" aria-live="polite">
                <span class="filter-finder-quiz__interest-confirmation-icon" aria-hidden="true"></span>
                <span class="filter-finder-quiz__interest-confirmation-text">${this.escapeHtml(
                  this.config.labels.interestConfirmation || ''
                )}</span>
              </div>
            </div>
          `
          : '';

        const buttonMarkup =
          hasCatalogProduct && resolvedUrl && ctaLabel
            ? `<a class="filter-finder-quiz__result-card-button" href="${this.escapeAttribute(resolvedUrl)}">${this.escapeHtml(
                ctaLabel
              )}</a>`
            : '';

        const mediaMarkup = resolvedImage
          ? `
            <div class="filter-finder-quiz__result-media filter-finder-quiz__result-media--image">
              <img
                class="filter-finder-quiz__result-image"
                src="${this.escapeAttribute(resolvedImage)}"
                alt="${this.escapeAttribute(resolvedImageAlt)}"
                loading="lazy"
              >
            </div>
          `
          : `
            <div class="filter-finder-quiz__result-media filter-finder-quiz__result-media--icon${!hasCatalogProduct ? ' filter-finder-quiz__result-media--missing' : ''}">
              <span class="filter-finder-quiz__result-icon" aria-hidden="true">${icon}</span>
            </div>
          `;

        return `
          <article class="filter-finder-quiz__result-card${primary ? ' filter-finder-quiz__result-card--primary' : ''}${!hasCatalogProduct ? ' filter-finder-quiz__result-card--missing-product' : ''}">
            <div class="filter-finder-quiz__result-card-top">
              ${
                badge
                  ? `<span class="filter-finder-quiz__badge filter-finder-quiz__badge--${this.escapeAttribute(
                      badgeTone
                    )}">${this.escapeHtml(badge)}</span>`
                  : ''
              }
            </div>
            ${mediaMarkup}
            <div class="filter-finder-quiz__result-card-body">
              <h4 class="filter-finder-quiz__result-card-title">${this.escapeHtml(resolvedTitle)}</h4>
              ${resolvedVariantTitle ? `<p class="filter-finder-quiz__result-card-variant">${this.escapeHtml(resolvedVariantTitle)}</p>` : ''}
              ${priceMarkup}
              ${noteMarkup}
              ${featuresMarkup}
            </div>
            <div class="filter-finder-quiz__result-card-actions">
              ${buttonMarkup}
              ${interestMarkup}
            </div>
          </article>
        `;
      }

      async submitQuizToKlaviyo(result, button) {
        const publicApiKey = this.config.klaviyo?.publicApiKey?.trim();
        const identity = this.getLeadIdentity();

        if (!publicApiKey || !identity.email) return;

        const properties = this.buildKlaviyoProperties(result);
        const signature = JSON.stringify({
          email: identity.email,
          listId: this.config.klaviyo?.listId?.trim() || '',
          properties,
        });

        if (signature === this.lastSubmittedSignature) return;

        const originalText = button?.textContent || '';

        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
          button.textContent = this.config.labels.saving || '';
        }

        try {
          const results = await Promise.allSettled([
            this.createKlaviyoClientProfile(publicApiKey, properties),
            this.createKlaviyoClientEvent(publicApiKey, properties),
            this.createKlaviyoClientSubscription(publicApiKey),
          ]);

          const failedRequests = results.filter((result) => result.status === 'rejected');
          const subscriptionResult = results[2];

          if (failedRequests.length > 0) {
            console.warn('Klaviyo quiz submit partially failed', failedRequests);
          }

          if (subscriptionResult?.status === 'fulfilled') {
            this.lastSubmittedSignature = signature;
          }
        } catch (error) {
          console.warn('Klaviyo quiz submit failed', error);
        } finally {
          if (button instanceof HTMLButtonElement) {
            button.textContent = originalText;
            button.disabled = false;
          }

          this.syncState();
        }
      }

      buildKlaviyoProperties(result) {
        const normalizedScope = this.getNormalizedFilterScope();
        const primaryOutcome = result.primaryOutcome || null;
        const recommendedProducts = Array.isArray(result.recommendedProducts) ? result.recommendedProducts.filter(Boolean) : [];
        const interestedProducts = this.getInterestedProducts();

        return {
          quiz_completed_at: new Date().toISOString(),
          quiz_version: this.config.env?.quizVersion || '1.2',
          quiz_source: this.config.klaviyo?.source || '',
          filter_scope: normalizedScope,
          tenure: this.wantsDrinking() ? this.answers.s2 || null : null,
          household_size: this.wantsDrinking() ? this.answers.s3 || null : null,
          water_concern: this.answers.s4 || null,
          drinking_format: this.wantsDrinking() ? this.answers.s6 || null : null,
          shower_type: this.wantsShower() ? this.answers.s5 || null : null,
          recommended_product: primaryOutcome,
          recommended_products: recommendedProducts,
          interested_products: interestedProducts,
          recommended_cartridge: this.getPrimaryRecommendedCartridge(primaryOutcome),
          is_preorder: recommendedProducts.some((outcomeKey) => this.getOutcomeStatus(outcomeKey) === 'preorder'),
          is_waitlist: recommendedProducts.some((outcomeKey) => this.getOutcomeStatus(outcomeKey) === 'waitlist'),
          detected_country: this.config.env?.detectedCountry || null,
          detected_city: null,
          entered_postcode: null,
          hardness_tier: null,
        };
      }

      async submitInterestToKlaviyo(outcomeKey, isSelected, button, interestWrap) {
        const identity = this.getLeadIdentity();

        if (!identity.email) return;

        const result = this.currentResult || this.getResult();
        const properties = {
          ...this.buildKlaviyoProperties(result),
          interest_updated_at: new Date().toISOString(),
          selected_interest_product: outcomeKey,
          selected_interest_state: isSelected ? 'selected' : 'removed',
        };
        const signature = JSON.stringify({
          email: identity.email,
          interested_products: properties.interested_products,
          selected_interest_product: properties.selected_interest_product,
          selected_interest_state: properties.selected_interest_state,
        });

        if (signature === this.lastInterestSignature) return;

        const originalText = button instanceof HTMLButtonElement ? button.textContent : '';
        const commitSavedState = () => {
          this.answers.savedInterestStates = {
            ...(this.answers.savedInterestStates || {}),
            [outcomeKey]: isSelected,
          };

          interestWrap?.classList.toggle('is-selected', isSelected);
          interestWrap?.classList.toggle('is-dirty', false);
          interestWrap?.classList.toggle('is-saved', isSelected);
        };

        const publicApiKey = this.config.klaviyo?.publicApiKey?.trim();

        if (!publicApiKey) {
          commitSavedState();

          if (button instanceof HTMLButtonElement) {
            button.disabled = true;
          }

          return;
        }

        try {
          if (button instanceof HTMLButtonElement) {
            button.disabled = true;
            button.textContent = this.config.labels.saving || '';
          }

          const results = await Promise.allSettled([
            this.createKlaviyoClientProfile(publicApiKey, properties),
            this.createKlaviyoClientEvent(
              publicApiKey,
              properties,
              this.config.klaviyo?.interestEventName || 'quiz_interest_update'
            ),
          ]);
          const failedRequests = results.filter((resultItem) => resultItem.status === 'rejected');

          if (failedRequests.length > 0) {
            console.warn('Klaviyo interest submit partially failed', failedRequests);
          }

          if (failedRequests.length < results.length) {
            this.lastInterestSignature = signature;
            commitSavedState();
          }
        } catch (error) {
          console.warn('Klaviyo interest submit failed', error);
        } finally {
          if (button instanceof HTMLButtonElement) {
            button.textContent = originalText;
            button.disabled = !interestWrap?.classList.contains('is-dirty');
          }
        }
      }

      getLeadIdentity() {
        const name = this.answers.lead?.name?.trim() || '';
        const email = this.answers.lead?.email?.trim().toLowerCase() || '';

        return {
          name,
          email,
        };
      }

      async createKlaviyoClientProfile(publicApiKey, properties) {
        const identity = this.getLeadIdentity();

        if (!identity.email) return;

        const response = await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(publicApiKey)}`, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: this.config.klaviyo?.revision || '2026-04-15',
          },
          body: JSON.stringify({
            data: {
              type: 'profile',
              attributes: {
                email: identity.email,
                first_name: identity.name || undefined,
                properties,
              },
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`Klaviyo profile request failed with status ${response.status}`);
        }
      }

      async createKlaviyoClientEvent(publicApiKey, properties, eventName = this.config.klaviyo?.eventName || 'quiz_complete') {
        const identity = this.getLeadIdentity();

        if (!identity.email) return;

        const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(publicApiKey)}`, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: this.config.klaviyo?.revision || '2026-04-15',
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties,
                metric: {
                  data: {
                    type: 'metric',
                    attributes: {
                      name: eventName,
                    },
                  },
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: identity.email,
                      first_name: identity.name || undefined,
                    },
                  },
                },
              },
            },
          }),
        });

        if (!response.ok) {
          throw new Error(`Klaviyo event request failed with status ${response.status}`);
        }
      }

      async createKlaviyoClientSubscription(publicApiKey) {
        const identity = this.getLeadIdentity();

        if (!identity.email) return;

        const listId = this.config.klaviyo?.listId?.trim();
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: identity.email,
                    first_name: identity.name || undefined,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED',
                          consented_at: new Date().toISOString(),
                        },
                      },
                    },
                  },
                },
              },
              custom_source: this.config.klaviyo?.source || undefined,
            },
          },
        };

        if (listId) {
          payload.data.relationships = {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          };
        }

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicApiKey)}`, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: this.config.klaviyo?.revision || '2026-04-15',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Klaviyo subscription request failed with status ${response.status}`);
        }
      }

      getIconMarkup(type) {
        const icons = {
          faucet: `
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 10.5H17C18.6569 10.5 20 11.8431 20 13.5V14.25" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M8 10.5V7.5C8 5.29086 9.79086 3.5 12 3.5H14.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M14.5 3.5V6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M17.5 17.25C17.5 18.3546 16.6046 19.25 15.5 19.25C14.3954 19.25 13.5 18.3546 13.5 17.25C13.5 16.1454 15.5 13.75 15.5 13.75C15.5 13.75 17.5 16.1454 17.5 17.25Z" fill="currentColor" fill-opacity="0.22" stroke="currentColor" stroke-width="1.2"/>
            </svg>
          `,
          gravity: `
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M8 4.5H16V8.5H8V4.5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
              <path d="M7 8.5H17L16 18.5H8L7 8.5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
              <path d="M10 12.75H14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          `,
          shower: `
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 8C5 5.79086 6.79086 4 9 4H14.5C16.9853 4 19 6.01472 19 8.5V9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M19 9H11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M11.5 12.5V12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M14.5 12.5V12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M17.5 12.5V12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M10.5 15.75V15.75" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M13.5 15.75V15.75" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M16.5 15.75V15.75" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          `,
          underCounter: `
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 5H19V19H5V5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
              <path d="M8.5 8.5H15.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M8.5 12H15.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M10 19V15.5H14V19" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
            </svg>
          `,
        };

        return icons[type] || '';
      }

      escapeHtml(value) {
        return String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      escapeAttribute(value) {
        return this.escapeHtml(value);
      }
    }
  );
}
